import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import {
  SupportAgent as AgentIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import AiTelephonyPinPanel from './AiTelephonyPinPanel';

/**
 * Einstellungen → „KI-Telefonist (fonio)" — White-Label-Selbsteinrichtung,
 * Muster analog AppPaymentSettingsSection.js (siehe KI-TELEFONIST_KONZEPT.md §15).
 *
 * Anzeigedaten kommen aus dem öffentlichen Doc appSettings/aiTelephony (keine
 * Secrets). Token-/Account-Verwaltung läuft über die Callables
 * connectAiTelephonyAccount / configureAiTelephony / setAiTelephonyActions /
 * disableAiTelephonyAccount (admin-gegatet im Backend).
 */
const ACTION_TOGGLES = [
  { key: 'allowBooking', label: 'Fahrt buchen', secondary: 'KI darf neue Fahrten anlegen' },
  { key: 'allowCancel', label: 'Fahrt stornieren', secondary: 'KI darf Fahrten des Anrufers stornieren' },
  { key: 'allowUpdate', label: 'Fahrt ändern', secondary: 'KI darf Uhrzeit/Ziel/Abholadresse ändern' },
  { key: 'allowFixedPrice', label: 'Festpreis-Auskunft', secondary: 'KI darf auf Kundenwunsch Festpreise nennen' }
];

const AiTelephonySettingsSection = ({ canManage = true }) => {
  // Aktivieren/Koppeln (Secrets) darf nur NextCab: User mit hiddenFromOperatorsPage===true.
  // Unternehmer-Admins steuern nur die Aktions-Toggles; alles andere übernimmt NextCab.
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.hiddenFromOperatorsPage === true;
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [fonioConnected, setFonioConnected] = useState(false);
  const [accountIdMasked, setAccountIdMasked] = useState('');
  const [activeAccountId, setActiveAccountId] = useState('');
  const [actions, setActions] = useState({
    allowBooking: true,
    allowCancel: true,
    allowUpdate: true,
    allowFixedPrice: true
  });

  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message }
  const [connectResult, setConnectResult] = useState(null); // { token, webhookUrl, accountId }

  // appSettings/aiTelephony live beobachten (öffentlich lesbar, keine Secrets).
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'appSettings', 'aiTelephony'), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      setEnabled(data.enabled === true);
      setFonioConnected(data.fonioConnected === true);
      setAccountIdMasked(data.accountIdMasked || '');
      setActiveAccountId(data.activeAccountId || '');
      setActions({
        allowBooking: data.allowBooking !== false,
        allowCancel: data.allowCancel !== false,
        allowUpdate: data.allowUpdate !== false,
        allowFixedPrice: data.allowFixedPrice !== false
      });
      setLoading(false);
    }, () => setLoading(false));
    return unsubscribe;
  }, []);

  const copy = (text) => {
    try {
      navigator.clipboard?.writeText(text);
      setFeedback({ severity: 'success', message: 'In die Zwischenablage kopiert.' });
    } catch (_) {
      // Clipboard nicht verfügbar — Wert ist weiterhin sichtbar zum manuellen Kopieren.
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setFeedback(null);
    try {
      const callable = httpsCallable(functions, 'connectAiTelephonyAccount');
      const result = await callable({ displayName: 'fonio KI-Telefonist' });
      setConnectResult(result.data || null);
      setFeedback({
        severity: 'success',
        message: 'fonio-Account erstellt. Token jetzt in fonio eintragen — er wird nur dieses eine Mal angezeigt.'
      });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Verbindung fehlgeschlagen.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleToggle = async () => {
    setSaving(true);
    setFeedback(null);
    const nextEnabled = !enabled;
    try {
      const result = await httpsCallable(functions, 'configureAiTelephony')({ enabled: nextEnabled });
      const serviceAreaActivated = result?.data?.serviceAreaActivated === true;
      setFeedback({
        severity: 'success',
        message: nextEnabled
          ? (serviceAreaActivated ? 'KI-Telefonist aktiviert — das Betriebsgebiet wurde dabei mitaktiviert.' : 'KI-Telefonist aktiviert.')
          : 'KI-Telefonist deaktiviert — Anrufe gehen an den Menschen.'
      });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Einstellung konnte nicht gespeichert werden.' });
    } finally {
      setSaving(false);
    }
  };

  const handleActionToggle = async (key) => {
    setSavingAction(key);
    setFeedback(null);
    try {
      await httpsCallable(functions, 'setAiTelephonyActions')({ [key]: !actions[key] });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Einstellung konnte nicht gespeichert werden.' });
    } finally {
      setSavingAction(null);
    }
  };

  const handleDisconnect = async () => {
    if (!activeAccountId) return;
    setConnecting(true);
    setFeedback(null);
    try {
      await httpsCallable(functions, 'disableAiTelephonyAccount')({ accountId: activeAccountId });
      setConnectResult(null);
      setFeedback({ severity: 'success', message: 'Verbindung getrennt. Das bisherige Token ist nun ungültig.' });
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'Trennen fehlgeschlagen.' });
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          <AgentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          KI-Telefonist (fonio)
          {enabled && <Chip label="Aktiv" color="success" size="small" sx={{ ml: 1 }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ein deutschsprachiger KI-Assistent nimmt Anrufe entgegen, bucht Fahrten,
          gibt Wartezeit/Status aus und übergibt bei Bedarf an einen Menschen.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {!canManage && (
            <Alert severity="info" sx={{ mx: 3, my: 2 }}>
              Der KI-Telefonist wird von der Hauptzentrale verwaltet.
            </Alert>
          )}

          <List disablePadding>
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={enabled}
                  onChange={handleToggle}
                  disabled={!canManage || !isSuperAdmin || saving || (!enabled && !fonioConnected)}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <AgentIcon />
              </ListItemIcon>
              <ListItemText
                primary="KI-Telefonist aktivieren"
                secondary={
                  enabled
                    ? 'Eingehende Anrufe werden vom KI-Assistenten bearbeitet'
                    : isSuperAdmin
                      ? (fonioConnected ? 'fonio ist verbunden — Schalter aktivieren, um zu starten' : 'Zuerst unten fonio verbinden')
                      : 'Die Aktivierung erfolgt durch NextCab'
                }
              />
            </ListItem>
          </List>

          {enabled && (
            <Box sx={{ px: 3, pt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Welche Aktionen darf die KI ausführen?
              </Typography>
              <List disablePadding>
                {ACTION_TOGGLES.map((toggle) => (
                  <ListItem
                    key={toggle.key}
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={actions[toggle.key]}
                        onChange={() => handleActionToggle(toggle.key)}
                        disabled={!canManage || savingAction !== null}
                        color="primary"
                      />
                    }
                  >
                    <ListItemText primary={toggle.label} secondary={toggle.secondary} />
                  </ListItem>
                ))}
              </List>
              <Alert severity="info" sx={{ mt: 1 }}>
                Gesperrte Aktionen sowie Krankenfahrten, Beschwerden und Sonderfälle
                übergibt die KI automatisch an einen Menschen.
              </Alert>
            </Box>
          )}

          {enabled && <AiTelephonyPinPanel />}

          <Box sx={{ px: 3, py: 2, bgcolor: 'surface.50' }}>
            {!isSuperAdmin ? (
              <>
                {fonioConnected ? (
                  <Alert severity="success">
                    Der KI-Telefonist ist von NextCab für Ihr Konto eingerichtet.
                  </Alert>
                ) : (
                  <Alert severity="info">
                    Der KI-Telefonist wird von NextCab aktiviert. Bitte kontaktieren Sie NextCab,
                    um ihn für Ihr Konto freizuschalten.
                  </Alert>
                )}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Was der KI-Assistent kann
                  </Typography>
                  <Typography variant="body2" color="text.secondary" component="div">
                    • Nimmt Anrufe rund um die Uhr in natürlichem Deutsch entgegen<br />
                    • Bucht Fahrten – sofort und als Vorbestellung – und erkennt Stammkunden<br />
                    • Versteht Orte und Geschäfte als Adresse (z. B. „Penny"), Großraum, Gepäck und Kartenzahlung<br />
                    • Nennt Wartezeit, Festpreis und geschätzten Fahrpreis<br />
                    • Storniert und ändert bestehende Fahrten<br />
                    • Übergibt Krankenfahrten, Beschwerden und Sonderfälle an einen Menschen
                  </Typography>
                </Box>
              </>
            ) : (<>
            {fonioConnected && !connectResult ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} sx={{ mb: 1 }}>
                <Alert severity="success" sx={{ flex: 1 }}>
                  fonio verbunden: <strong>{accountIdMasked || 'Account aktiv'}</strong>
                </Alert>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={handleConnect} disabled={connecting}>
                    Token erneuern
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={handleDisconnect} disabled={connecting}>
                    Trennen
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  fonio verbinden
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  Es wird ein Zugangs-Token und eine Webhook-URL erzeugt. Beides tragen
                  Sie in fonio als Bearer-Token bzw. als Ziel der „Custom Action" ein.
                  Das Token wird aus Sicherheitsgründen nur ein einziges Mal angezeigt.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleConnect}
                  disabled={connecting}
                  startIcon={connecting ? <CircularProgress size={16} /> : <AgentIcon />}
                >
                  {connecting ? 'Wird erstellt…' : 'fonio verbinden'}
                </Button>
              </Box>
            )}

            {connectResult && (
              <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setConnectResult(null)}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Token und Webhook-URL jetzt in fonio eintragen — das Token wird nicht erneut angezeigt!
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Bearer-Token</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {connectResult.token}
                      </Typography>
                      <IconButton size="small" onClick={() => copy(connectResult.token)}>
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Webhook-URL</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {connectResult.webhookUrl}
                      </Typography>
                      <IconButton size="small" onClick={() => copy(connectResult.webhookUrl)}>
                        <CopyIcon fontSize="inherit" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
              </Alert>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Richten Sie anschließend in fonio die Anrufweiterleitung Ihrer Zentralnummer
                auf die fonio-Nummer ein (absolut oder als Überlauf). Krankenfahrten und
                Beschwerden werden grundsätzlich an einen Menschen übergeben.
              </Typography>
            </Alert>
            </>)}

            {feedback && (
              <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default AiTelephonySettingsSection;
